import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  seancesApi, SEANCE_TYPES, SEANCE_STATUSES, VOTE_OPTIONS,
  TYPE_LABEL, STATUS_LABEL, VOTE_LABEL,
} from '../../services/seancesApi';
import { getToken } from '../auth';
import './admin-form.css';

const EMPTY_FORM = {
  reference: '',
  title: '',
  type: 'ordinaire',
  status: 'annoncee',
  sessionDate: '',
  location: '',
  summary: '',
  pvUrl: '',
  agenda: [],
  participants: [],
  decisions: [],
  order: 0,
};

function isoToInputDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_PILL = { annoncee: 'warn', tenue: 'ocean', publiee: 'live' };
const TYPE_PILL   = { ordinaire: '', extraordinaire: 'sunset', bureau: 'gold' };

const Seances = () => {
  const token = getToken();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType,   setFilterType]   = useState('all');
  const [query,        setQuery]        = useState('');

  const [open,       setOpen]       = useState(false);
  const [editingId,  setEditingId]  = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await seancesApi.adminList(token, {
        status: filterStatus !== 'all' ? filterStatus : undefined,
        type:   filterType   !== 'all' ? filterType   : undefined,
        q: query || undefined,
        limit: 200,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filterStatus, filterType, query]);

  useEffect(() => { load(); }, [load]);

  const kpi = useMemo(() => {
    const c = { total: items.length, annoncee: 0, tenue: 0, publiee: 0 };
    for (const s of items) c[s.status] = (c[s.status] || 0) + 1;
    return c;
  }, [items]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, sessionDate: isoToInputDate(new Date().toISOString()) });
    setSaveError(null);
    setOpen(true);
  };

  const openEdit = async (id) => {
    setEditingId(id);
    setSaveError(null);
    try {
      const { seance } = await seancesApi.adminGet(token, id);
      setForm({
        reference: seance.reference || '',
        title: seance.title,
        type: seance.type,
        status: seance.status,
        sessionDate: isoToInputDate(seance.sessionDate),
        location: seance.location || '',
        summary: seance.summary || '',
        pvUrl: seance.pvUrl || '',
        agenda: seance.agenda || [],
        participants: seance.participants || [],
        decisions: seance.decisions || [],
        order: seance.order || 0,
      });
      setOpen(true);
    } catch (err) {
      alert('Impossible de charger la séance : ' + err.message);
    }
  };

  const close = useCallback(() => {
    setOpen(false); setEditingId(null); setForm(EMPTY_FORM); setSaveError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [open, close]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        reference:    form.reference,
        title:        form.title,
        type:         form.type,
        status:       form.status,
        sessionDate:  new Date(form.sessionDate).toISOString(),
        location:     form.location,
        summary:      form.summary,
        pvUrl:        form.pvUrl,
        agenda:       form.agenda.filter((a) => a.titre),
        participants: form.participants.filter((p) => p.name),
        decisions:    form.decisions.filter((d) => d.libelle),
        order:        parseInt(form.order, 10) || 0,
      };
      if (editingId) await seancesApi.adminUpdate(token, editingId, payload);
      else           await seancesApi.adminCreate(token, payload);
      close();
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id, title) => {
    if (!window.confirm(`Supprimer définitivement la séance « ${title} » ?`)) return;
    try {
      await seancesApi.adminDelete(token, id);
      await load();
    } catch (err) {
      alert('Suppression impossible : ' + err.message);
    }
  };

  const onAdvanceStatus = async (s) => {
    const next = s.status === 'annoncee' ? 'tenue' : s.status === 'tenue' ? 'publiee' : null;
    if (!next) return;
    const lbl = next === 'tenue' ? 'Marquer comme tenue' : 'Publier le PV';
    if (!window.confirm(`${lbl} pour « ${s.title} » ?`)) return;
    try {
      await seancesApi.adminUpdate(token, s.id, { status: next });
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  // --- éditeurs des sous-listes ---
  const addAgenda      = () => setForm((f) => ({ ...f, agenda:       [...f.agenda,       { titre: '', rapporteur: '' }] }));
  const addParticipant = () => setForm((f) => ({ ...f, participants: [...f.participants, { name: '', role: '', present: true }] }));
  const addDecision    = () => setForm((f) => ({ ...f, decisions:    [...f.decisions,    { libelle: '', vote: 'adopte', tally: '' }] }));

  const removeAt = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));
  const updateAt = (key, i, patch) => setForm((f) => ({
    ...f,
    [key]: f[key].map((it, idx) => idx === i ? { ...it, ...patch } : it),
  }));

  if (!token) {
    return (
      <div className="content" style={{ padding: 40 }}>
        <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', color: 'var(--char-500)' }}>
          Session expirée. Reconnectez-vous.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="topbar">
        <div className="breadcrumb">
          <span>Cambérène</span><span className="sep">/</span>
          <span>Admin</span><span className="sep">/</span>
          <b>Séances &amp; PV</b>
        </div>
        <div className="search">
          <span className="ic" />
          <input
            type="text"
            placeholder="Chercher (titre, ref, lieu)…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd>⌘ K</kbd>
        </div>
        <div className="top-actions">
          <button className="icon-btn" title="Rafraîchir" onClick={load} aria-label="Rafraîchir">↻</button>
          <button className="top-cta" onClick={openCreate}>
            <span className="plus" /> Nouvelle séance
          </button>
        </div>
      </div>

      <div className="content">
        <div className="page-head">
          <div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
              Conseil municipal · {total} séance{total > 1 ? 's' : ''}
            </span>
            <h1>Les délibérations,<br /><span style={{ fontStyle: 'italic' }}>en transparence.</span></h1>
            <p className="sub">Annoncer l'ordre du jour, enregistrer la séance, publier le PV. Trois statuts, un flux clair.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="pill-btn" style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}>
              {SEANCE_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.lbl}</option>)}
            </select>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
              className="pill-btn" style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}>
              {SEANCE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.lbl}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
          {[
            { lbl: 'Total',        val: kpi.total,    color: 'var(--char-400)' },
            { lbl: 'Annoncées',    val: kpi.annoncee, color: '#E8B53C' },
            { lbl: 'Tenues',       val: kpi.tenue,    color: '#1B6985' },
            { lbl: 'Publiées',     val: kpi.publiee,  color: '#34A853' },
          ].map((k) => (
            <div key={k.lbl} className="kpi">
              <span className="lab">
                <span className="dot" style={{ background: k.color }} />
                {k.lbl}
              </span>
              <b>{k.val}</b>
            </div>
          ))}
        </div>

        {error && (
          <div className="bulkbar on" style={{ background: 'rgba(230,57,70,.12)', color: '#B72333' }}>
            <span>⚠</span><span>Erreur : {error}</span>
          </div>
        )}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Réf.</th>
                <th>Titre</th>
                <th>Type</th>
                <th>Statut</th>
                <th>Date</th>
                <th>Ordre du jour</th>
                <th>Décisions</th>
                <th style={{ width: 140 }} />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>Chargement…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)' }}>
                  Aucune séance. Cliquez « Nouvelle séance » pour démarrer.
                </td></tr>
              )}
              {items.map((s) => {
                const canAdvance = s.status !== 'publiee';
                const nextLabel  = s.status === 'annoncee' ? 'Marquer tenue' : 'Publier PV';
                const nextIcon   = s.status === 'annoncee' ? '▶' : '✓';
                return (
                  <tr key={s.id} onClick={() => openEdit(s.id)}>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.06em', color: 'var(--char-700)', fontWeight: 500 }}>{s.reference || '—'}</td>
                    <td>
                      <b style={{ fontWeight: 500, fontSize: 14 }}>{s.title}</b>
                      {s.location && <small style={{ display: 'block', marginTop: 3, fontSize: 12, color: 'var(--char-500)' }}>📍 {s.location}</small>}
                    </td>
                    <td><span className={`cat-pill ${TYPE_PILL[s.type] || ''}`}>{TYPE_LABEL[s.type] || s.type}</span></td>
                    <td><span className={`status-pill ${STATUS_PILL[s.status] || ''}`}>{STATUS_LABEL[s.status] || s.status}</span></td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>{fmtDate(s.sessionDate)}</td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-700)' }}>{s.agenda.length} pt{s.agenda.length > 1 ? 's' : ''}</td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-700)' }}>{s.decisions.length}</td>
                    <td onClick={(ev) => ev.stopPropagation()}>
                      <div className="row-actions" style={{ opacity: 1 }}>
                        {canAdvance && (
                          <button title={nextLabel} onClick={() => onAdvanceStatus(s)}
                            style={s.status === 'tenue' ? { background: '#34A853', color: 'var(--bone)', borderColor: '#34A853' } : {}}>
                            {nextIcon}
                          </button>
                        )}
                        <button title="Éditer" onClick={() => openEdit(s.id)}>✎</button>
                        <button title="Supprimer" className="danger" onClick={() => onDelete(s.id, s.title)}>✕</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="table-foot">
            <span>{total} séance{total > 1 ? 's' : ''}</span>
            <span>{items.length} affichée{items.length > 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* ============ MODALE ============ */}
      {open && (
        <div className="admin-modal-bg" onClick={close}>
          <div className="admin-modal" onClick={(ev) => ev.stopPropagation()} style={{ maxWidth: 820 }}>
            <div className="admin-modal-head">
              <div>
                <span className="admin-modal-eyebrow">
                  {editingId ? `Édition · ${form.reference}` : 'Nouvelle séance'}
                </span>
                <h2 className="admin-modal-title">
                  {editingId ? form.title : 'Programmer une séance'}
                </h2>
              </div>
              <button onClick={close} className="admin-modal-close" aria-label="Fermer">✕</button>
            </div>

            <form className="admin-form" onSubmit={onSubmit}>
              {/* --- bandeau titre + meta --- */}
              <label className="admin-field">
                <span className="admin-field-label">Titre *</span>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Ex : Séance ordinaire de juin 2026"
                  required
                />
              </label>

              <div className="admin-form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Type</span>
                  <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                    {SEANCE_TYPES.filter((t) => t.id !== 'all').map((t) => <option key={t.id} value={t.id}>{t.lbl}</option>)}
                  </select>
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Statut</span>
                  <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                    {SEANCE_STATUSES.filter((s) => s.id !== 'all').map((s) => <option key={s.id} value={s.id}>{s.lbl}</option>)}
                  </select>
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Date &amp; heure *</span>
                  <input
                    type="datetime-local"
                    value={form.sessionDate}
                    onChange={(e) => setForm((f) => ({ ...f, sessionDate: e.target.value }))}
                    required
                  />
                </label>
              </div>

              <div className="admin-form-row" style={{ gridTemplateColumns: '1fr 1.4fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Référence (laissez vide pour auto)</span>
                  <input
                    type="text"
                    value={form.reference}
                    onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
                    placeholder="Ex : SC-2026-06"
                  />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Lieu</span>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                    placeholder="Salle du conseil — mairie"
                  />
                </label>
              </div>

              <label className="admin-field">
                <span className="admin-field-label">Résumé / Contexte (public)</span>
                <textarea
                  rows={2}
                  value={form.summary}
                  onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
                  placeholder="Ex : Séance ordinaire centrée sur la voirie et l'éducation."
                />
              </label>

              {/* --- Ordre du jour --- */}
              <div className="admin-field">
                <span className="admin-field-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                  Ordre du jour ({form.agenda.length} pt{form.agenda.length > 1 ? 's' : ''})
                  <button type="button" onClick={addAgenda} className="admin-btn" style={{ padding: '4px 10px', fontSize: 11 }}>+ Ajouter</button>
                </span>
                {form.agenda.length === 0 && (
                  <small style={{ color: 'var(--char-500)', fontStyle: 'italic' }}>Aucun point. Ajoutez les délibérations prévues.</small>
                )}
                {form.agenda.map((it, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 8, marginTop: 6 }}>
                    <input
                      type="text"
                      placeholder="Intitulé du point"
                      value={it.titre}
                      onChange={(e) => updateAt('agenda', i, { titre: e.target.value })}
                    />
                    <input
                      type="text"
                      placeholder="Rapporteur"
                      value={it.rapporteur}
                      onChange={(e) => updateAt('agenda', i, { rapporteur: e.target.value })}
                    />
                    <button type="button" onClick={() => removeAt('agenda', i)} className="admin-btn" style={{ padding: '4px 10px', color: '#B72333' }}>✕</button>
                  </div>
                ))}
              </div>

              {/* --- Participants --- */}
              <div className="admin-field">
                <span className="admin-field-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                  Participants ({form.participants.length})
                  <button type="button" onClick={addParticipant} className="admin-btn" style={{ padding: '4px 10px', fontSize: 11 }}>+ Ajouter</button>
                </span>
                {form.participants.map((p, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr auto auto', gap: 8, marginTop: 6, alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="Nom complet"
                      value={p.name}
                      onChange={(e) => updateAt('participants', i, { name: e.target.value })}
                    />
                    <input
                      type="text"
                      placeholder="Rôle (maire, adjoint…)"
                      value={p.role}
                      onChange={(e) => updateAt('participants', i, { role: e.target.value })}
                    />
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--char-700)' }}>
                      <input
                        type="checkbox"
                        checked={p.present}
                        onChange={(e) => updateAt('participants', i, { present: e.target.checked })}
                      />
                      Présent
                    </label>
                    <button type="button" onClick={() => removeAt('participants', i)} className="admin-btn" style={{ padding: '4px 10px', color: '#B72333' }}>✕</button>
                  </div>
                ))}
              </div>

              {/* --- Décisions --- */}
              <div className="admin-field">
                <span className="admin-field-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                  Décisions ({form.decisions.length})
                  <button type="button" onClick={addDecision} className="admin-btn" style={{ padding: '4px 10px', fontSize: 11 }}>+ Ajouter</button>
                </span>
                {form.decisions.length === 0 && (
                  <small style={{ color: 'var(--char-500)', fontStyle: 'italic' }}>Aucune décision enregistrée. Visible uniquement quand la séance est publiée.</small>
                )}
                {form.decisions.map((d, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, marginTop: 6 }}>
                    <input
                      type="text"
                      placeholder="Libellé de la délibération"
                      value={d.libelle}
                      onChange={(e) => updateAt('decisions', i, { libelle: e.target.value })}
                    />
                    <select
                      value={d.vote}
                      onChange={(e) => updateAt('decisions', i, { vote: e.target.value })}
                    >
                      {VOTE_OPTIONS.map((v) => <option key={v.id} value={v.id}>{v.lbl}</option>)}
                    </select>
                    <input
                      type="text"
                      placeholder="Tally (12-2-1)"
                      value={d.tally}
                      onChange={(e) => updateAt('decisions', i, { tally: e.target.value })}
                    />
                    <button type="button" onClick={() => removeAt('decisions', i)} className="admin-btn" style={{ padding: '4px 10px', color: '#B72333' }}>✕</button>
                  </div>
                ))}
              </div>

              <label className="admin-field">
                <span className="admin-field-label">URL du PV (PDF officiel) — visible quand statut = publiée</span>
                <input
                  type="url"
                  value={form.pvUrl}
                  onChange={(e) => setForm((f) => ({ ...f, pvUrl: e.target.value }))}
                  placeholder="https://…/pv-2026-06.pdf"
                />
              </label>

              {saveError && <div className="admin-form-error">{saveError}</div>}

              <div className="admin-form-foot">
                <button type="button" onClick={close} className="admin-btn">Annuler</button>
                <button type="submit" disabled={saving} className="admin-btn admin-btn-primary">
                  {saving ? 'Enregistrement…' : (editingId ? 'Enregistrer' : 'Créer la séance')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Seances;
